import { Send } from "lucide-react";
import { useRef, useState } from "react";
import { COLORS, RADIUS, SPACE, TYPE } from "../theme/index.js";
import { MentionPicker } from "./MentionPicker.js";
import { type MentionContext, detectMentionContext } from "./mention-detect.js";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  placeholder?: string;
}

export function ChatInput({
  value,
  onChange,
  onSend,
  disabled = false,
  placeholder = "Ask anything... (Enter to send, @ to mention an agent)",
}: ChatInputProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [mention, setMention] = useState<MentionContext>({
    active: false,
    query: "",
    atIndex: -1,
  });

  function recomputeMention() {
    const el = ref.current;
    if (!el) return;
    const ctx = detectMentionContext(el.value, el.selectionStart ?? el.value.length);
    setMention(ctx);
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    onChange(e.target.value);
    // setTimeout to read selection after DOM update
    setTimeout(recomputeMention, 0);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // If picker is active, let MentionPicker handle Up/Down/Enter/Tab/Escape via capture phase.
    if (mention.active && ["ArrowUp", "ArrowDown", "Enter", "Tab", "Escape"].includes(e.key)) {
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && value.trim()) onSend();
    }
  }

  function handleSelectMention(slug: string) {
    if (mention.atIndex < 0) return;
    const el = ref.current;
    const cursor = el?.selectionStart ?? value.length;
    const before = value.slice(0, mention.atIndex);
    const after = value.slice(cursor);
    const inserted = `@${slug} `;
    const next = `${before}${inserted}${after}`;
    onChange(next);
    setMention({ active: false, query: "", atIndex: -1 });
    setTimeout(() => {
      const newCursor = before.length + inserted.length;
      el?.setSelectionRange(newCursor, newCursor);
      el?.focus();
    }, 0);
  }

  return (
    <div
      style={{
        flexShrink: 0,
        padding: `${SPACE[3]}px`,
        borderTop: `1px solid ${COLORS.border}`,
        position: "relative",
      }}
    >
      {mention.active && (
        <MentionPicker
          query={mention.query}
          onSelect={handleSelectMention}
          onClose={() => setMention({ active: false, query: "", atIndex: -1 })}
        />
      )}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: SPACE[2],
          background: COLORS.surface,
          border: `1px solid ${COLORS.borderHigh}`,
          borderRadius: RADIUS.xl,
          padding: `${SPACE[2]}px ${SPACE[3]}px`,
        }}
      >
        <textarea
          ref={ref}
          data-testid="chat-input"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onKeyUp={recomputeMention}
          onClick={recomputeMention}
          placeholder={placeholder}
          rows={1}
          disabled={disabled}
          style={{
            flex: 1,
            background: "transparent",
            border: "none",
            outline: "none",
            resize: "none",
            fontSize: TYPE.size.sm,
            color: COLORS.text,
            maxHeight: 120,
            lineHeight: 1.5,
            fontFamily: "inherit",
          }}
        />
        <button
          type="button"
          data-testid="chat-send"
          onClick={onSend}
          disabled={disabled || !value.trim()}
          aria-label="Send"
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: RADIUS.full,
            border: "none",
            cursor: disabled || !value.trim() ? "not-allowed" : "pointer",
            background: disabled || !value.trim() ? COLORS.surface : COLORS.brand,
            color: disabled || !value.trim() ? COLORS.textDim : "#fff",
            transition: "all 120ms ease",
          }}
        >
          <Send size={13} />
        </button>
      </div>
      <p
        style={{
          fontSize: TYPE.size.xs,
          color: COLORS.textGhost,
          textAlign: "center",
          margin: `${SPACE[1]}px 0 0`,
        }}
      >
        Enter to send · Shift+Enter for newline · @ to switch agents
      </p>
    </div>
  );
}
